<template>
    <th :style="widthStyle" @click="onHeaderClick" :class="headerClass">
        <div class="opThHead">
            <span class="opThLabel">
                <slot />
            </span>
        </div>
        <div class="opTdFilterContainer" :class="{ dateRange: filterType == 'dateRange'}" v-if="hasFilter" @click.stop>
            <input v-if="isSimpleInput" class="opTdFilter" :type="inputType" :name="filter" v-model="textModel" @input="commitText" :placeholder="placeholder"/>

            <input v-else-if="filterType == 'checkbox'" class="opTdFilter" :class="{ indeterminate: triState == 1 }" type="checkbox" :checked="triState == 2" ref="checkboxEl" @click.stop="cycleTriState"/>

            <template v-else-if="filterType == 'dateRange'">
                <div>
                    Dal <input class="opTdFilter" type="date" :name="filterFrom" v-model="rangeFrom" @input="commitRange"/>
                </div>
                <div>
                    Al <input class="opTdFilter" type="date" :name="filterTo"   v-model="rangeTo"   @input="commitRange"/>
                </div>
            </template>

            <template v-else-if="filterType == 'numberRange'">
                <div>
                    Dal <input class="opTdFilter" type="number" :name="filterFrom" v-model.number="rangeFrom" @input="commitRange"/>
                </div>
                <div>
                    Al <input class="opTdFilter" type="number" :name="filterTo"   v-model.number="rangeTo"   @input="commitRange"/>
                </div>
            </template>

            <template v-else-if="filterType == 'select'">
                <select class="opTdFilter" v-model="textModel" @change="commitText">
                    <option :value="''"></option>
                    <option v-for="opt in selectOptions" :value="opt">{{opt}}</option>
                </select>
            </template>

        </div>
    </th>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { OP_TABLE_CTX, type OpTableContext, type SortDir, type ColumnMeta } from './opTable.context';
import { Prop } from 'vue-property-decorator';

@Options({
    inject: {
        opTableCtx: { from: OP_TABLE_CTX as any, default: null }
    }
})
export default class OpTh extends Vue {
    // props
    @Prop() width!: string | number | null;
    @Prop() filter!: string | undefined;
    @Prop() sort!: string | undefined;
    @Prop() filterType!: ColumnMeta['filterType'];
    @Prop() filterFrom!: string | undefined;
    @Prop() filterTo!: string | undefined;
    @Prop() selectOptions!: string[];
    @Prop() placeholder!: string;
    
    // inject
    opTableCtx!: OpTableContext | null;
    
    // state
    textModel: any = '';
    triState = 0;
    rangeFrom: any = '';
    rangeTo: any = '';
    
    get widthStyle() { 
        return this.width ? { width: typeof this.width === 'number' ? `${this.width}px` : String(this.width) } : null; 
    }
    get hasFilter() { 
        return !!this.filter; 
    }
    get isSimpleInput() { 
        return !this.filterType || this.filterType === 'text' || this.filterType === 'date' || this.filterType === 'number'; 
    }
    get inputType() { 
        return this.filterType === 'date' ? 'date' : this.filterType === 'number' ? 'number' : 'text'; 
    }
    get sortState() { 
        return this.opTableCtx?.getSort() ?? { key: undefined, dir: '' as SortDir }; 
    }
    get isSorted() { 
        return !!this.sort && this.sortState.key === this.sort; 
    }
    get headerClass() { 
        return { 
            sortable: !!this.sort,
            asc: this.isSorted && this.sortState.dir === 'asc', 
            desc: this.isSorted && this.sortState.dir === 'desc' 
        }; 
    }
    
    mounted() {

        // inizializza UI dagli eventuali valori salvati
        if (this.filter && this.isSimpleInput) {
            const cur = this.opTableCtx?.getFilterValue(this.filter);
            if (cur != null) this.textModel = cur;
        }
        if (this.filter && this.filterType === 'checkbox') {
            const cur = Number(this.opTableCtx?.getFilterValue(this.filter) ?? 0);
            this.triState = cur;
        }
        if (this.filter && (this.filterType === 'dateRange' || this.filterType === 'numberRange')) {
            const cur = this.opTableCtx?.getFilterValue(this.filter) || {};
            this.rangeFrom = cur.from ?? '';
            this.rangeTo   = cur.to ?? '';
        }
    }
    
    onHeaderClick() {
        if (!this.sort || !this.opTableCtx)
            return;

        const { key, dir } = this.sortState;

        let next: SortDir = 'asc';
        if (key === this.sort){
            next = dir === 'asc' ? 'desc' : dir === 'desc' ? '' : 'asc';
        }
        this.opTableCtx.setSort(this.sort, next);
    }
    
    commitText() {
        if (!this.opTableCtx || !this.filter) return;
        this.opTableCtx.setFilterValue(this.filter, (this.textModel ?? '').toString(), {
            key: this.filter, filterType: this.filterType
        });
    }
    cycleTriState() {
        this.triState = (this.triState + 1) % 3;
        if (this.opTableCtx && this.filter) {
            this.opTableCtx.setFilterValue(this.filter, this.triState, { key: this.filter, filterType: 'checkbox' });
        }
    }
    commitRange() {
        if (!this.opTableCtx || !this.filter) return;
        this.opTableCtx.setFilterValue(this.filter, { from: this.rangeFrom, to: this.rangeTo }, {
            key: this.filter, filterType: this.filterType, fromKey: this.filterFrom, toKey: this.filterTo
        });
    }
}
</script>
